import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AdminLayout from "../../../layouts/admin/AdminLayout";
import api from "../../../api/axios";
import DOMPurify from "dompurify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

export default function AdminEmploymentDetail() {
  const { id } = useParams();
  const nav = useNavigate();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 편집용 상태
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [locationText, setLocationText] = useState("");
  const [summary, setSummary] = useState("");
  const [deadline, setDeadline] = useState(""); // yyyy-mm-dd
  const [detailUrl, setDetailUrl] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [content, setContent] = useState("");

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [infoMsg, setInfoMsg] = useState("");

  const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd}`;
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/api/employments/admin/detail/${id}`);
      const resultCode = res.data?.resultCode;

      if (resultCode !== "200" && resultCode !== 200) {
        setError(
          res.data?.resultMessage || "채용공고 상세를 불러오지 못했습니다.",
        );
        setItem(null);
        return;
      }

      const data = res.data?.data || null;
      setItem(data);

      // 편집용 초기값 세팅
      setTitle(data?.title || "");
      setCompany(data?.company || "");
      setLocationText(data?.locationText || "");
      setSummary(data?.summary || "");
      setDeadline(data?.deadline || "");
      setDetailUrl(data?.detailUrl || "");
      setIsActive(Boolean(data?.isActive));
      setContent(data?.content || "");
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.resultMessage ||
          "채용공고 상세를 불러오는 중 오류가 발생했습니다.",
      );
      setItem(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const renderContent = () => {
    if (!item?.content) return null;
    const safeHtml = DOMPurify.sanitize(item.content);
    return (
      <div
        className="prose max-w-none text-sm leading-relaxed text-slate-800 prose-a:text-blue-600 prose-a:underline prose-img:rounded-xl prose-img:shadow-sm md:text-base"
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    );
  };

  const onEdit = () => {
    if (!item) return;
    setTitle(item.title || "");
    setCompany(item.company || "");
    setLocationText(item.locationText || "");
    setSummary(item.summary || "");
    setDeadline(item.deadline || "");
    setDetailUrl(item.detailUrl || "");
    setIsActive(Boolean(item.isActive));
    setContent(item.content || "");
    setInfoMsg("");
    setError("");
    setEditing(true);
  };

  const onCancelEdit = () => {
    if (item) {
      setTitle(item.title || "");
      setCompany(item.company || "");
      setLocationText(item.locationText || "");
      setSummary(item.summary || "");
      setDeadline(item.deadline || "");
      setDetailUrl(item.detailUrl || "");
      setIsActive(Boolean(item.isActive));
      setContent(item.content || "");
    }
    setEditing(false);
    setInfoMsg("");
  };

  const validate = () => {
    if (!title.trim()) return "제목을 입력해 주세요.";
    if (!company.trim()) return "회사명을 입력해 주세요.";
    if (!locationText.trim()) return "근무지(텍스트)를 입력해 주세요.";
    if (!summary.trim()) return "요약을 입력해 주세요.";
    if (!deadline) return "마감일을 선택해 주세요.";

    const plain = content.replace(/<[^>]+>/g, "").trim();
    if (!plain) return "내용을 입력해 주세요.";

    if (detailUrl.trim()) {
      try {
        // eslint-disable-next-line no-new
        new URL(detailUrl.trim());
      } catch {
        return "상세 링크(URL) 형식이 올바르지 않습니다.";
      }
    }
    return "";
  };

  const onSave = async () => {
    setError("");
    setInfoMsg("");

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setSaving(true);
    try {
      const sanitizedContent = DOMPurify.sanitize(content);

      const payload = {
        title: title.trim(),
        company: company.trim(),
        locationText: locationText.trim(),
        summary: summary.trim(),
        content: sanitizedContent,
        deadline, // yyyy-mm-dd
        detailUrl: detailUrl.trim() || null,
        isActive,
      };

      const res = await api.put(`/api/employments/${id}`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const resultCode = res.data?.resultCode;
      if (resultCode === "200" || resultCode === 200) {
        setInfoMsg("채용공고가 수정되었습니다.");
        setEditing(false);

        const updated = res.data?.data;
        if (updated) {
          setItem(updated);
          setTitle(updated.title || "");
          setCompany(updated.company || "");
          setLocationText(updated.locationText || "");
          setSummary(updated.summary || "");
          setDeadline(updated.deadline || "");
          setDetailUrl(updated.detailUrl || "");
          setIsActive(Boolean(updated.isActive));
          setContent(updated.content || "");
        } else {
          load();
        }
      } else {
        setError(
          res.data?.resultMessage ||
            "채용공고 수정에 실패했습니다. 다시 시도해 주세요.",
        );
      }
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.resultMessage ||
          "채용공고 수정 중 오류가 발생했습니다.",
      );
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("정말 이 채용공고를 삭제하시겠습니까?")) return;

    setDeleting(true);
    setError("");
    setInfoMsg("");
    try {
      const res = await api.delete(`/api/employments/${id}`);
      const resultCode = res.data?.resultCode;

      // 너 컨트롤러는 NO_CONTENT(204)를 내려주고 있어서 둘 다 허용
      if (
        resultCode === "200" ||
        resultCode === 200 ||
        resultCode === "204" ||
        resultCode === 204
      ) {
        alert("채용공고가 삭제되었습니다.");
        nav("/admin/employment/list");
      } else {
        setError(
          res.data?.resultMessage ||
            "채용공고 삭제에 실패했습니다. 다시 시도해 주세요.",
        );
      }
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.resultMessage ||
          "채용공고 삭제 중 오류가 발생했습니다.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "link",
    "image",
  ];

  return (
    <AdminLayout>
      {/* 헤더 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-blue-900 md:text-3xl">
            채용공고 상세
          </h1>
          <p className="mt-1 text-xs text-slate-500">ID: {id}</p>
        </div>

        <div className="flex gap-2">
          {!editing && (
            <>
              <button
                type="button"
                onClick={onEdit}
                className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                수정
              </button>
              <button
                type="button"
                onClick={onDelete}
                disabled={deleting}
                className="rounded-xl border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
              >
                {deleting ? "삭제 중..." : "삭제"}
              </button>
            </>
          )}

          {editing && (
            <>
              <button
                type="button"
                onClick={onCancelEdit}
                className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                취소
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? "저장 중..." : "저장"}
              </button>
            </>
          )}

          <button
            type="button"
            onClick={() => nav("/admin/employment/list")}
            className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            목록으로
          </button>
        </div>
      </div>

      {/* 본문 카드 */}
      <div className="rounded-2xl border bg-white p-6 shadow-sm md:p-8">
        {loading && (
          <div className="py-10 text-center text-slate-500">
            채용공고를 불러오는 중입니다...
          </div>
        )}

        {!loading && error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {infoMsg && !loading && (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
            {infoMsg}
          </div>
        )}

        {!loading && !error && !item && (
          <div className="py-10 text-center text-slate-500">
            해당 채용공고를 찾을 수 없습니다.
          </div>
        )}

        {!loading && !error && item && (
          <>
            {/* ====== 읽기 모드 ====== */}
            {!editing && (
              <>
                <header className="border-b pb-4 md:pb-5">
                  <div className="mb-1 text-xs text-slate-500">
                    작성일 : {fmtDate(item.createdAt)}
                    {item.updatedAt && (
                      <>
                        {"  ·  "}수정일 : {fmtDate(item.updatedAt)}
                      </>
                    )}
                    {"  ·  "}마감일 : {fmtDate(item.deadline)}
                  </div>

                  <h2 className="text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">
                    {item.title}
                  </h2>

                  <div className="mt-2 text-sm text-slate-700">
                    <span className="font-semibold">{item.company}</span>
                    {item.locationText ? ` · ${item.locationText}` : ""}
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${
                        item.isActive
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-slate-200 bg-slate-50 text-slate-600"
                      }`}
                    >
                      {item.isActive ? "공개" : "비공개"}
                    </span>

                    {item.detailUrl && (
                      <a
                        href={item.detailUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center rounded-lg border bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        상세 링크 열기
                      </a>
                    )}
                  </div>
                </header>

                {/* 요약 */}
                {item.summary && (
                  <section className="mt-5 rounded-xl border bg-slate-50 p-4 text-sm text-slate-700">
                    <div className="mb-1 text-xs font-semibold text-slate-500">
                      요약
                    </div>
                    <div className="whitespace-pre-line">{item.summary}</div>
                  </section>
                )}

                {/* 내용 */}
                <section className="mt-6">{renderContent()}</section>

                <div className="mt-8 flex justify-between border-t pt-4 text-xs text-slate-500">
                  <div>ID: {item.id}</div>
                  <Link
                    to={`/employment/${item.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center rounded-lg border bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    사용자 화면에서 보기
                  </Link>
                </div>
              </>
            )}

            {/* ====== 편집 모드 ====== */}
            {editing && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  onSave();
                }}
                className="space-y-4"
              >
                {/* 메타 */}
                <div className="text-xs text-slate-500">
                  작성일 : {fmtDate(item.createdAt)}
                  {item.updatedAt && (
                    <>
                      {"  ·  "}수정일 : {fmtDate(item.updatedAt)}
                    </>
                  )}
                </div>

                <Field label="제목" required>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="제목을 입력해 주세요."
                  />
                </Field>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="회사명" required>
                    <input
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="회사명을 입력해 주세요."
                    />
                  </Field>

                  <Field label="근무지(텍스트)" required>
                    <input
                      value={locationText}
                      onChange={(e) => setLocationText(e.target.value)}
                      className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="예) 말레이시아(훈련센터) / 부산 / 재택 등"
                    />
                  </Field>
                </div>

                <Field label="요약" required>
                  <textarea
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    rows={3}
                    className="w-full resize-none rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="리스트에 노출될 짧은 요약을 입력해 주세요."
                  />
                </Field>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="마감일" required>
                    <input
                      type="date"
                      value={deadline}
                      onChange={(e) => setDeadline(e.target.value)}
                      className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </Field>

                  <Field label="상세 링크(URL)">
                    <input
                      value={detailUrl}
                      onChange={(e) => setDetailUrl(e.target.value)}
                      className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="예) https://example.com/job/123 (선택)"
                    />
                  </Field>
                </div>

                <Field label="공개 여부">
                  <div className="flex items-center gap-3">
                    <input
                      id="isActive"
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="h-4 w-4"
                    />
                    <label
                      htmlFor="isActive"
                      className="text-sm text-slate-700"
                    >
                      공개(사용자에게 노출)
                    </label>
                  </div>
                </Field>

                <Field label="내용" required>
                  <div className="rounded-xl border">
                    <ReactQuill
                      theme="snow"
                      value={content}
                      onChange={setContent}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="채용공고 내용을 입력해 주세요."
                    />
                  </div>
                  <span className="text-xs text-slate-500">
                    간단한 서식, 링크, 이미지 등을 사용할 수 있습니다.
                  </span>
                </Field>
              </form>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="grid gap-1 text-sm">
      <div className="font-medium text-slate-800">
        {label}
        {required && <span className="text-rose-600"> *</span>}
      </div>
      {children}
    </div>
  );
}
